import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/lib/motion";
import { useContent, buildWa } from "@/lib/ContentProvider";

const EASE = [0.76, 0, 0.24, 1];

// Wraps every page for enter/exit transition
export const Page = ({ children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.div>
);

// Inner page hero (sub pages)
export const PageHero = ({ overline, titleLines, subtitle }) => (
  <section className="relative overflow-hidden bg-alch-forest px-5 pt-40 pb-24 text-alch-bg md:px-8 md:pt-48 md:pb-32">
    <div className="mx-auto max-w-7xl">
      {overline && (
        <Reveal>
          <span className="text-xs uppercase tracking-[0.24em] text-alch-terracotta">{overline}</span>
        </Reveal>
      )}
      <MaskedLines
        as="h1"
        lines={titleLines}
        delay={0.15}
        className="mt-6 max-w-5xl font-serif text-5xl leading-[1.03] tracking-tight md:text-7xl lg:text-[5rem]"
      />
      {subtitle && (
        <Reveal delay={0.4}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-alch-bg/70">{subtitle}</p>
        </Reveal>
      )}
    </div>
  </section>
);

export const Overline = ({ children, className = "" }) => (
  <span className={`text-xs uppercase tracking-[0.24em] text-alch-terracotta ${className}`}>{children}</span>
);

export const PrimaryButton = ({ href, to, children, className = "", ...rest }) => {
  const cls = `group inline-flex items-center gap-2 rounded-full bg-alch-forest px-7 py-4 text-sm font-medium text-alch-bg transition-transform duration-300 hover:scale-[1.04] ${className}`;
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={cls} {...rest}>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </a>
  );
};

// CTA band used at bottom of most pages
export const CTABand = () => {
  const { siteConfig } = useContent();
  return (
  <section className="bg-alch-bg px-5 py-24 md:px-8 md:py-32">
    <Reveal className="mx-auto max-w-6xl overflow-hidden rounded-[2rem] bg-alch-terracotta px-8 py-16 text-center text-white md:px-16 md:py-24">
      <Overline className="text-white/80">Your recovery starts here</Overline>
      <h2 className="mx-auto mt-6 max-w-3xl font-serif text-4xl leading-tight md:text-6xl">
        Book your consultation with our surgeons today
      </h2>
      <p className="mx-auto mt-6 max-w-xl text-white/85">
        Chat with our team on WhatsApp — quick, private, and personal.
      </p>
      <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
        <a href={buildWa(siteConfig.whatsapp)} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="rounded-full bg-white px-8 py-4 text-sm font-medium text-alch-forest transition-transform hover:scale-[1.04]">
          Book on WhatsApp
        </a>
        <a href={`tel:${siteConfig.phoneRaw}`} data-testid="cta-call" className="rounded-full border border-white/60 px-8 py-4 text-sm font-medium text-white transition-colors hover:bg-white/10">
          Call {siteConfig.phone}
        </a>
      </div>
    </Reveal>
  </section>
  );
};

// WhatsApp appointment form — composes a prefilled message
export const AppointmentForm = () => {
  const { siteConfig, services } = useContent();
  const treatments = services.flatMap((s) => s.items);
  const [form, setForm] = useState({ name: "", phone: "", treatment: treatments[0] || "General Consultation", date: "", note: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const msg =
      `Hello, I would like to book an appointment.\n\n` +
      `Name: ${form.name || "-"}\n` +
      `Phone: ${form.phone || "-"}\n` +
      `Treatment: ${form.treatment}\n` +
      `Preferred date: ${form.date || "Flexible"}\n` +
      (form.note ? `Note: ${form.note}\n` : "");
    window.open(buildWa(siteConfig.whatsapp, msg), "_blank");
  };

  return (
    <form onSubmit={submit} data-testid="appointment-form" className="rounded-[1.75rem] border border-alch-forest/12 bg-alch-surface p-6 md:p-9">
      <div className="grid gap-5 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.16em] text-alch-muted">Full Name</span>
          <input data-testid="appt-name" required value={form.name} onChange={set("name")} placeholder="Your name"
            className="mt-2 w-full rounded-xl border border-alch-forest/15 bg-alch-bg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-alch-terracotta" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.16em] text-alch-muted">Phone</span>
          <input data-testid="appt-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..."
            className="mt-2 w-full rounded-xl border border-alch-forest/15 bg-alch-bg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-alch-terracotta" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.16em] text-alch-muted">Treatment</span>
          <select data-testid="appt-treatment" value={form.treatment} onChange={set("treatment")}
            className="mt-2 w-full rounded-xl border border-alch-forest/15 bg-alch-bg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-alch-terracotta">
            {treatments.map((t) => <option key={t}>{t}</option>)}
            <option>General Consultation</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.16em] text-alch-muted">Preferred Date</span>
          <input data-testid="appt-date" type="date" value={form.date} onChange={set("date")}
            className="mt-2 w-full rounded-xl border border-alch-forest/15 bg-alch-bg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-alch-terracotta" />
        </label>
      </div>
      <label className="mt-5 block">
        <span className="text-xs uppercase tracking-[0.16em] text-alch-muted">Message (optional)</span>
        <textarea data-testid="appt-note" rows={3} value={form.note} onChange={set("note")} placeholder="Briefly describe your concern"
          className="mt-2 w-full rounded-xl border border-alch-forest/15 bg-alch-bg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-alch-terracotta" />
      </label>
      <button type="submit" data-testid="appt-submit"
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-alch-forest px-8 py-4 text-sm font-medium text-alch-bg transition-transform hover:scale-[1.02]">
        Continue on WhatsApp
        <ArrowUpRight className="h-4 w-4" />
      </button>
      <p className="mt-4 text-center text-xs text-alch-muted">We&rsquo;ll open WhatsApp with your details prefilled — just hit send.</p>
    </form>
  );
};
